import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Cake } from 'lucide-react';
import type { BirthdayEntry } from '@optiflow/types';
import { Alert } from '@/components/ui/Alert';
import { Badge } from '@/components/ui/Badge';
import { Card, CardHeader } from '@/components/ui/Card';
import { DataTable, type Column } from '@/components/ui/DataTable';
import { apiErrorMessage } from '@/services/apiClient';
import { useBirthdaysQuery } from '../api/crm.api';

const CONSENT_TONE: Record<string, 'neutral' | 'success' | 'danger'> = {
  NOT_GRANTED: 'neutral',
  GRANTED: 'success',
  REVOKED: 'danger',
};

/**
 * Whose birthday it is, for a person to notice.
 *
 * There is no send button, and that is the feature. A birthday greeting is marketing, marketing
 * needs a recorded yes, and the consent column is here so anybody reaching for the phone can see
 * whether this customer ever gave one. Nothing automatic will ever message these people.
 */
export function BirthdaysPanel() {
  const { t, i18n } = useTranslation();
  const { data, isPending, isError, error, refetch } = useBirthdaysQuery(14);

  const today = new Date().toISOString().slice(0, 10);
  const columns: Column<BirthdayEntry>[] = [
    {
      key: 'customer',
      header: t('crm.fields.customer'),
      primary: true,
      render: (row) => (
        <Link className="text-brand underline" to={`/customers/${row.customerId}`}>
          <span dir="auto">{row.displayName}</span>
        </Link>
      ),
    },
    {
      key: 'number',
      header: t('crm.fields.customerNumber'),
      hideOnCard: true,
      render: (row) => <span dir="ltr">{row.customerNumber}</span>,
    },
    {
      key: 'observedOn',
      header: t('crm.fields.observedOn'),
      render: (row) => (
        <span>
          <span dir="ltr">{new Date(row.observedOn).toLocaleDateString(i18n.language)}</span>{' '}
          {row.observedOn === today ? <Badge tone="brand">{t('crm.birthdays.today')}</Badge> : null}
        </span>
      ),
    },
    {
      key: 'consent',
      header: t('crm.fields.marketingConsent'),
      render: (row) => (
        <Badge tone={CONSENT_TONE[row.marketingConsentStatus] ?? 'neutral'}>
          {t(`crm.consent.${row.marketingConsentStatus}`)}
        </Badge>
      ),
    },
  ];

  return (
    <Card>
      <CardHeader
        title={t('crm.birthdays.title')}
        subtitle={data ? t('crm.birthdays.count', { count: data.length }) : undefined}
      />

      <div className="px-4 pt-3">
        <Alert tone="info">{t('crm.birthdays.noAutoSend')}</Alert>
      </div>

      <DataTable
        caption={t('crm.birthdays.tableCaption')}
        columns={columns}
        rows={data}
        rowKey={(row) => row.customerId}
        isLoading={isPending}
        isError={isError}
        errorMessage={apiErrorMessage(error) ?? t('crm.birthdays.loadError')}
        onRetry={() => void refetch()}
        emptyIcon={Cake}
        emptyTitle={t('crm.birthdays.empty')}
        emptyDescription={t('crm.birthdays.emptyHint')}
      />
    </Card>
  );
}
